// Suvaani's social profiles as a row of round icon links, used in the landing footer and the course footer.

import type { FC, ReactNode, SVGProps } from 'react'
import type { SocialId, SocialLink } from '@/constants/site'

import { Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'
import { cn } from '@/lib/utils'

import { SOCIAL_LINKS } from '@/constants/site'

interface SocialLinksProps {
    /** Whose profiles these are, for screen readers ("Suvaani", "Prakrit Suvaani"). */
    brand?: string
    /** Defaults to Suvaani's own profiles. */
    links?: SocialLink[]
    className?: string
}

/** The X mark (lucide has no X logo, only a close icon). */
const XMark: FC<SVGProps<SVGSVGElement>> = (props): ReactNode => (
    <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' {...props}>
        <path d='M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z' />
    </svg>
)

const ICON: Record<SocialId, FC<{ className?: string }>> = {
    linkedin: Linkedin,
    instagram: Instagram,
    youtube: Youtube,
    facebook: Facebook,
    x: XMark
}

export const SocialLinks: FC<SocialLinksProps> = ({ brand = 'Suvaani', links = SOCIAL_LINKS, className }): ReactNode => (
    <ul aria-label={`${brand} on social media`} className={cn('flex flex-wrap items-center gap-2', className)}>
        {links.map(link => {
            const Icon = ICON[link.id]

            return (
                <li key={link.id}>
                    <a
                        href={link.href}
                        target='_blank'
                        rel='noopener noreferrer'
                        aria-label={`${brand} on ${link.label} (opens in a new tab)`}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-border bg-background text-foreground/75 transition-colors hover:border-saffron/60 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-saffron'
                    >
                        <Icon className={link.id === 'x' ? 'size-4' : 'size-5'} />
                    </a>
                </li>
            )
        })}
    </ul>
)
